import React, { useEffect, useMemo, useState } from "react";
import { api, API } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Loader2, X, Download, RefreshCw, Filter } from "lucide-react";
import { PageHeader, StatusBadge, fmtUSD, fmtDateTime, Empty } from "../../components/admin/ui";

const SOURCES = ["stripe", "paypal", "cashapp", "zelle", "bank", "cash", "other"];
const STATUSES = ["confirmed", "pending", "failed", "refunded"];

const MetricTile = ({ label, total, count, testid }) => (
  <div className="bento-card !p-5" data-testid={testid}>
    <span className="glow-blob" />
    <div className="overline">{label}</div>
    <div className="font-serif-display text-3xl text-[#F6F0E6] mt-2">{fmtUSD(total)}</div>
    <div className="text-[11px] text-[#D9D9D9]/60 mt-0.5">{count} gift{count === 1 ? "" : "s"}</div>
  </div>
);

const Donations = () => {
  const [metrics, setMetrics] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    source: "cashapp", amount_usd: "", donor_name: "", donor_email: "", note: "",
  });
  const [filter, setFilter] = useState({ source: "", status: "", q: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [mRes, lRes] = await Promise.all([
        api.get("/admin/donations/metrics"),
        api.get("/admin/donations", { params: { limit: 500 } }),
      ]);
      setMetrics(mRes.data);
      setItems(lRes.data.donations || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.amount_usd || parseFloat(form.amount_usd) <= 0) { toast.error("Enter a valid amount."); return; }
    setSaving(true);
    try {
      await api.post("/admin/donations/external", { ...form, amount_usd: parseFloat(form.amount_usd) });
      toast.success("Donation logged.");
      setShowForm(false);
      setForm({ source: "cashapp", amount_usd: "", donor_name: "", donor_email: "", note: "" });
      load();
    } catch { toast.error("Failed to log donation."); }
    finally { setSaving(false); }
  };

  const downloadCSV = () => {
    window.open(`${API}/admin/donations/export.csv`, "_blank");
  };

  const filtered = useMemo(() => items.filter((d) => {
    if (filter.source && d.source !== filter.source) return false;
    if (filter.status && d.status !== filter.status) return false;
    if (filter.q) {
      const q = filter.q.toLowerCase();
      if (!(d.donor_name || "").toLowerCase().includes(q) && !(d.donor_email || "").toLowerCase().includes(q)) return false;
    }
    return true;
  }), [items, filter.source, filter.status, filter.q]);

  return (
    <div className="space-y-8" data-testid="admin-donations">
      <PageHeader
        overline="Stewardship"
        title={<><span className="italic gold-text">Partners</span> & Donations</>}
        subtitle="Stripe gifts arrive automatically. Cash App, PayPal, Zelle, and bank gifts are entered manually below — no fake data, ever."
      >
        <button onClick={load} className="btn-ghost !py-2.5 !px-4 !text-sm" data-testid="refresh-btn"><RefreshCw className="h-3.5 w-3.5" /> Refresh</button>
        <button onClick={downloadCSV} className="btn-ghost !py-2.5 !px-4 !text-sm" data-testid="export-csv-btn"><Download className="h-3.5 w-3.5" /> Export CSV</button>
        <button onClick={() => setShowForm(true)} className="btn-fire" data-testid="log-donation-btn"><Plus className="h-4 w-4" /> Log Donation</button>
      </PageHeader>

      {/* Metric strip */}
      {metrics && (
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetricTile testid="m-today" label="Today" total={metrics.today.total} count={metrics.today.count} />
          <MetricTile testid="m-week" label="This Week" total={metrics.week.total} count={metrics.week.count} />
          <MetricTile testid="m-month" label="This Month" total={metrics.month.total} count={metrics.month.count} />
          <MetricTile testid="m-ytd" label="Year to Date" total={metrics.ytd.total} count={metrics.ytd.count} />
        </section>
      )}

      {/* Filters */}
      <section className="bento-card !p-5">
        <span className="glow-blob" />
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-xs text-[#C9A44C] tracking-[0.18em] uppercase"><Filter className="h-3.5 w-3.5" /> Filter</div>
          <select className="bg-[#0a0a0a] border border-[#C9A44C]/25 rounded-full px-3 py-1.5 text-xs text-[#F6F0E6] focus:outline-none" value={filter.source} onChange={(e) => setFilter({ ...filter, source: e.target.value })} data-testid="filter-source">
            <option value="">All platforms</option>
            {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className="bg-[#0a0a0a] border border-[#C9A44C]/25 rounded-full px-3 py-1.5 text-xs text-[#F6F0E6] focus:outline-none" value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} data-testid="filter-status">
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input className="bg-[#0a0a0a] border border-[#C9A44C]/25 rounded-full px-3 py-1.5 text-xs text-[#F6F0E6] focus:outline-none flex-1 min-w-[180px]" placeholder="Search by donor name or email" value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} data-testid="filter-q" />
        </div>
      </section>

      {/* Table */}
      <section className="bento-card !p-0 overflow-hidden">
        <span className="glow-blob" />
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="donations-table">
            <thead>
              <tr className="text-left text-[10px] tracking-[0.22em] uppercase text-[#C9A44C] border-b border-[#C9A44C]/15">
                <th className="px-6 py-4">Date</th>
                <th className="px-6 py-4">Donor</th>
                <th className="px-6 py-4 hidden md:table-cell">Email</th>
                <th className="px-6 py-4">Amount</th>
                <th className="px-6 py-4">Platform</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 hidden xl:table-cell">Transaction</th>
                <th className="px-6 py-4 hidden lg:table-cell">Note</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="px-6 py-10 text-center text-[#D9D9D9]/60"><Loader2 className="h-4 w-4 inline animate-spin mr-2" />Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} className="px-6 py-12 text-center text-[#D9D9D9]/60">No donations match these filters. Real gifts will appear here as they come in.</td></tr>
              ) : filtered.map((d) => (
                <tr key={d.id} className="border-b border-[#C9A44C]/8 hover:bg-[#C9A44C]/5">
                  <td className="px-6 py-4 text-[#D9D9D9] whitespace-nowrap">{fmtDateTime(d.created_at)}</td>
                  <td className="px-6 py-4 text-[#F6F0E6]">{d.donor_name || "Anonymous"}</td>
                  <td className="px-6 py-4 hidden md:table-cell text-[#D9D9D9]/70">{d.donor_email || "—"}</td>
                  <td className="px-6 py-4 font-serif-display text-lg text-[#F6F0E6]">{fmtUSD(d.amount_usd)}</td>
                  <td className="px-6 py-4"><span className="text-[11px] tracking-[0.18em] uppercase text-[#C9A44C]">{d.source}</span></td>
                  <td className="px-6 py-4"><StatusBadge status={d.status} /></td>
                  <td className="px-6 py-4 hidden xl:table-cell text-[#D9D9D9]/60 font-mono text-[11px] truncate max-w-[120px]">{d.stripe_session_id || "—"}</td>
                  <td className="px-6 py-4 hidden lg:table-cell text-[#D9D9D9]/60 truncate max-w-xs">{d.note || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Manual donation modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-[#050505]/85 backdrop-blur-md flex items-center justify-center px-6 py-8" onClick={() => setShowForm(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bento-card !p-8 max-w-lg w-full space-y-5" data-testid="log-donation-form">
            <span className="glow-blob" />
            <div className="flex items-center justify-between">
              <div><div className="overline">External Gift</div><h2 className="font-serif-display text-2xl text-[#F6F0E6] mt-1">Log a donation</h2></div>
              <button type="button" onClick={() => setShowForm(false)} className="h-9 w-9 rounded-full border border-[#C9A44C]/30 inline-flex items-center justify-center"><X className="h-4 w-4" /></button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="field-label">Platform</label>
                <select className="input-fire" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} data-testid="donation-source">
                  {SOURCES.filter((s) => s !== "stripe").map((s) => <option key={s} value={s} style={{ background: "#111" }}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="field-label">Amount (USD)</label>
                <input type="number" step="0.01" min="0.01" className="input-fire" value={form.amount_usd} onChange={(e) => setForm({ ...form, amount_usd: e.target.value })} placeholder="0.00" data-testid="donation-amount" />
              </div>
            </div>
            <div>
              <label className="field-label">Donor Name</label>
              <input className="input-fire" value={form.donor_name} onChange={(e) => setForm({ ...form, donor_name: e.target.value })} placeholder="Anonymous" data-testid="donation-donor-name" />
            </div>
            <div>
              <label className="field-label">Donor Email <span className="opacity-60 normal-case tracking-normal">(optional)</span></label>
              <input type="email" className="input-fire" value={form.donor_email} onChange={(e) => setForm({ ...form, donor_email: e.target.value })} data-testid="donation-donor-email" />
            </div>
            <div>
              <label className="field-label">Note</label>
              <textarea rows={2} className="input-fire" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="e.g. Cash App reference, designation" />
            </div>
            <button type="submit" disabled={saving} className="btn-fire w-full" data-testid="donation-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              {saving ? "Saving…" : "Log Donation"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default Donations;
